import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { instance } from '~/utils/api/api';
import { useToastError } from '~/utils/useToastError';
import Page from '~/components/Shared/Layout/Page/Page';
import Heading from '~/components/Shared/Heading/Heading';

import styles from './CreateAnnouncement.module.scss';
import {
  AnnouncementForm,
  AnnouncementValues,
} from '~/components/AnnouncementForm';
import { useTranslation } from 'react-i18next';
import { useToastSuccess } from '~/utils/useToastSuccess';

const CreateAnnouncement = (): JSX.Element => {
  const navigate = useNavigate();
  const toastError = useToastError();
  const { t } = useTranslation();
  const toastSuccess = useToastSuccess();

  const client = useQueryClient();

  const { mutateAsync: createAnnouncement } = useMutation(
    async ({
      module,
      status_id,
      name,
      text,
      start_date,
      end_date,
      photos,
      announcement_accommodations,
      announcement_organizations,
      tags,
      organization_all,
      tag_all,
    }: AnnouncementValues) => {
      const organization_ids = announcement_organizations.map(
        organization => organization.id
      );
      const tag_ids = tags.map(item => item.id);
      const response = await instance.post('admin/announcement', {
        module: String(module),
        status_id,
        name,
        text,
        start_date: start_date?.startOf('day').format('YYYY-MM-DD HH:mm:ss'),
        end_date: end_date?.endOf('day').format('YYYY-MM-DD HH:mm:ss'),
        image_ids: photos.map(photo => photo.id),
        accommodation_ids: announcement_accommodations.map(
          organization => organization.id
        ),
        ...(organization_all ? {} : { organization_ids }),
        ...(tag_all ? {} : { tag_ids }),
        ...(organization_all ? { organization_all } : {}),
        ...(tag_all ? { tag_all } : {}),
      });

      return response.data;
    },
    {
      onSuccess: () => {
        toastSuccess(t('news_created'));
        client.invalidateQueries(['announcements']);
        navigate('/announcements');
      },
      onError: error => {
        toastError(error);
      },
    }
  );
  return (
    <Page
      heading={
        <Heading
          text={t('creating_news')}
          classNameText={styles.createAnnouncementText}
        />
      }
    >
      <AnnouncementForm
        onSubmit={createAnnouncement}
        storageKey="create-announcement"
      />
    </Page>
  );
};

export default CreateAnnouncement;
